import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { from, Observable, of, throwError, timer } from 'rxjs';
import { catchError, mergeMap, retry, switchMap, toArray } from 'rxjs/operators';
import { DataPage, DataQuery } from '../../models/data-source';

export interface FileItem {
  id: string; parentId: string | null; name: string; isFolder: boolean; isShared: boolean;
  uploadedByUserId: number | null; uploadedByName: string | null; uploadedAt: string | null;
  isTemporary: boolean; version: number; updatedAt: string; contentType: string | null; byteSize: number | null; status: string | null;
}
export interface FileRevision { id: string; contentType: string; byteSize: number; status: string; createdAt: string; }
export interface FileProcessing { id: string; revisionId: string; status: string; text: string | null; error: string | null; createdAt: string; }
export interface FileSummary { id: string; revisionId: string; summary: string; createdAt: string; }
/** Larger files go through the chunked upload; both limits match FileLogic. */
const SINGLE_UPLOAD_MAX_BYTES = 10 * 1024 * 1024;
export const FILE_UPLOAD_MAX_BYTES = 1024 * 1024 * 1024;
const PARALLEL_PARTS = 3;
export interface FileUploadResult { id: string; revisionId: string; }
interface ChunkedUpload { uploadId: string; fileId: string; partSize: number; partCount: number; expiresAt: string; }
export interface FileDetail { file: FileItem; revisions: FileRevision[]; processing: FileProcessing[]; hasMoreHistory: boolean; summary?: FileSummary | null; }

@Injectable({ providedIn: 'root' })
export class FilesService {
  constructor(private http: HttpClient) {}
  list(query: DataQuery, area: string, parentId: string | null) {
    let params = new HttpParams().set('area', area).set('search', query.search).set('page', query.page).set('pageSize', query.pageSize);
    if (query.sort) params = params.set('sort', query.sort).set('desc', !!query.desc);
    if (parentId) params = params.set('parentId', parentId);
    return this.http.get<DataPage<FileItem>>('/api/files', { params });
  }
  get(id: string) { return this.http.get<FileDetail>('/api/files/' + id); }
  folder(name: string, parentId: string | null, isShared = false) {
    return this.http.post<string>('/api/files/folders', { name, parentId, isShared });
  }
  upload(file: File, parentId: string | null, isShared: boolean, fileId?: string): Observable<FileUploadResult> {
    if (file.size > SINGLE_UPLOAD_MAX_BYTES) return this.uploadInParts(file, parentId, isShared, fileId);
    const data = new FormData();
    data.set('file', file); data.set('isShared', String(isShared));
    if (parentId) data.set('parentId', parentId);
    if (fileId) data.set('fileId', fileId);
    return this.http.post<FileUploadResult>('/api/files/upload', data);
  }
  /** Sends the parts a few at a time, retrying a failed part; any other failure abandons the upload. */
  private uploadInParts(file: File, parentId: string | null, isShared: boolean, fileId?: string): Observable<FileUploadResult> {
    return this.http.post<ChunkedUpload>('/api/files/uploads', { name: file.name, byteSize: file.size, parentId, isShared, fileId: fileId ?? null }).pipe(
      switchMap(upload => from(Array.from({ length: upload.partCount }, (_, i) => i + 1)).pipe(
        mergeMap(part => this.uploadPart(file, upload, part), PARALLEL_PARTS),
        toArray(),
        switchMap(() => this.http.post<FileUploadResult>(`/api/files/uploads/${upload.uploadId}/complete`, {})),
        catchError(error => this.http.delete<void>(`/api/files/uploads/${upload.uploadId}`).pipe(
          catchError(() => of(null)), switchMap(() => throwError(() => error)))),
      )));
  }
  private uploadPart(file: File, upload: ChunkedUpload, part: number) {
    const blob = file.slice((part - 1) * upload.partSize, part * upload.partSize);
    return from(blob.arrayBuffer().then(async body => ({ body, hash: await crypto.subtle.digest('SHA-256', body) }))).pipe(
      switchMap(({ body, hash }) => this.http.put<void>(`/api/files/uploads/${upload.uploadId}/parts/${part}`, body, {
        headers: { 'Content-Type': 'application/octet-stream', 'x-part-sha256': btoa(String.fromCharCode(...new Uint8Array(hash))) },
      })),
      // A rejected part (4xx) will not succeed on a second try; a dropped or failed request may.
      retry({ count: 2, delay: (error: unknown) =>
        error instanceof HttpErrorResponse && error.status >= 400 && error.status < 500 ? throwError(() => error) : timer(1000) }),
    );
  }
  update(file: FileItem, name: string, parentId: string | null) {
    return this.http.put<void>('/api/files/' + file.id, { version: file.version, name, parentId });
  }
  delete(file: FileItem) {
    return file.isFolder
      ? this.http.delete<void>('/api/files/folders/' + file.id, { params: { version: file.version, recursive: true } })
      : this.http.delete<void>('/api/files/' + file.id, { params: { version: file.version } });
  }
  content(id: string, revisionId?: string) {
    let params = new HttpParams().set('preview', true);
    if (revisionId) params = params.set('revisionId', revisionId);
    return this.http.get(`/api/files/${id}/content`, { params, responseType: 'blob' }).pipe(
      catchError(async (error: unknown) => {
        // Angular also receives JSON failures as blobs for a binary request.
        if (error instanceof HttpErrorResponse && error.error instanceof Blob) {
          try {
            const body: unknown = JSON.parse(await error.error.text());
            error = new HttpErrorResponse({
              error: body, headers: error.headers, status: error.status,
              statusText: error.statusText, url: error.url ?? undefined,
            });
          } catch { /* Preserve the original response when the body is not JSON. */ }
        }
        throw error;
      }),
    );
  }
  summary(id: string) { return this.http.post<FileSummary>(`/api/files/${id}/summary`, {}); }
  ocr(id: string, revisionId: string) { return this.http.post<FileProcessing>(`/api/files/${id}/ocr/${revisionId}`, {}); }
  processing(id: string, runId: string) { return this.http.get<FileProcessing>(`/api/files/${id}/processing/${runId}`); }
  refresh(id: string, runId: string) { return this.http.post<FileProcessing>(`/api/files/${id}/processing/${runId}/refresh`, {}); }
}
